/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/features/attendanceModern/useAttendanceHomeExtras.ts
 * PURPOSE: SC-017-EMP-ATTENDANCE [1] home — data for the 3 mutually-exclusive
 *          branches (DEFAULT/IN-SHIFT/STALE-OPEN) that useAttendanceModernClock
 *          does NOT already cover: the weekly schedule grid + urgent/ad-hoc
 *          task cards (DEFAULT), and stale-open (ca tồn đọng) detection
 *          (STALE-OPEN). Split out of useAttendanceModernClock.ts (which stays
 *          scoped to the clock-in/out submission flow) — mirrors mobile's own
 *          separation into independent hooks (useWeekSchedule, taskApi.getMyTasks,
 *          usePreCheckinView all live apart from the attendance mutation hook).
 *
 *          STALE-OPEN detection deviates from the mobile hook it mirrors in
 *          one load-bearing way — see the `checkStaleOpen` doc-comment below
 *          for why: `GET /api/v2/attendance/pre-checkin-view` (which mobile's
 *          `usePreCheckinView` wraps) does not actually return the `is_stale`/
 *          `clock_in_at` fields mobile's own TypeScript types declare for it
 *          (backend/app/schemas/v2/attendance_sprint72.py:70-76 has neither
 *          field, and the endpoint body — attendance_sprint72.py:99-136 —
 *          only ever queries TODAY's attendance). Wiring web to that same
 *          contract would make STALE-OPEN permanently unreachable (a Vacuous
 *          Green per this repo's Debugging Pattern #15 — a branch that
 *          "looks built" but the trigger condition can never fire). Instead
 *          this hook derives the identical business rule ("≥1 attendance
 *          record with clock_out IS NULL dated before today") from the
 *          already-real, already-wired self-history endpoint
 *          (`GET /api/v2/attendance/history` via managerProfileApi.getHistory,
 *          used today by AttendanceHistory.tsx) — same data, same rule, no
 *          backend change needed.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-04
 * COMPLIANCE: SDLC 6.5.0 + Zero-Mock Tolerance (all 3 data sources are real,
 *   already-deployed endpoints — no fabricated/hardcoded data)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce, Employee Self-Service Attendance
 * - Impact: UX Critical — SC-017-EMP-ATTENDANCE [1] home is the single
 *   canonical attendance surface (registry §Mục đích)
 * - Stakeholders: Employees, Branch Managers (self-scope only here)
 *
 * @technical-architecture
 * - Layer: Feature Layer, Business Modules
 * - Pattern: Feature-Based Architecture, plain useState/useEffect (matches
 *   useAttendanceModernClock.ts — no React Query in this feature directory)
 * - Dependencies: React 18, TypeScript, apiV2 (axios), scheduleApi, managerProfileApi
 *
 * @cross-references
 * - Wireframe: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md
 *   §SC-017-EMP-ATTENDANCE, ANNOTATION [1] (lines ~6507-6531)
 * - Mobile source of truth: mobile/src/screens/attendance/AttendanceScreen.tsx
 *   (`:594-637` STALE-OPEN, `:639-664` IN-SHIFT, `:670-684` week schedule card,
 *   `:686-714` task cards)
 * - Backend (tasks): backend/app/api/v2/tasks.py get_my_tasks() → GET /tasks/my
 * - Backend (history/stale-open source): backend/app/api/v2/endpoints/attendance.py
 *   get_attendance_history() → GET /attendance/history
 * - Backend (week schedule): backend/app/api/v1/schedule.py my-schedule (via
 *   frontend/src/services/api/schedule.ts scheduleApi.getMySchedule)
 * - Consumer: frontend/src/pages/AttendanceModern.tsx
 *  */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { apiV2 } from '@/services/api/config'
import { scheduleApi } from '@/services/api/schedule'
import { managerProfileApi } from '@/services/api/managerProfileApi'
import { useAuthStore } from '@/store/auth'
import { toLocalISODate } from '@/utils/date'

/** [1] home DEFAULT — "Lịch ca tuần này" 7-day grid (today bolded by the caller). */
export interface WeekScheduleDay {
  date: string // YYYY-MM-DD
  hasShift: boolean
  isToday: boolean
}

/** [1] home DEFAULT — "Việc khẩn cấp"/"Việc phát sinh" card items. */
export interface HomeTaskItem {
  id: string
  title: string
  deadline: string | null // ISO datetime, may be null (no deadline set)
}

/** [1] home STALE-OPEN — the most recent still-open attendance from before today. */
export interface StaleOpenInfo {
  attendanceId: string
  clockInAt: string // ISO datetime
}

function startOfWeekMonday(base: Date): Date {
  const d = new Date(base)
  const day = d.getDay() // 0=Sun..6=Sat
  const diffToMonday = day === 0 ? -6 : 1 - day
  d.setDate(d.getDate() + diffToMonday)
  d.setHours(0, 0, 0, 0)
  return d
}

/** Backend TaskResponse (backend/app/schemas/v2/tasks.py) — only the fields this card needs. */
interface TaskApiItem {
  id: string
  task_type: string
  title: string
  deadline: string | null
}
interface TaskListApiResponse {
  items: TaskApiItem[]
  total: number
}

export function useAttendanceHomeExtras() {
  const { accessToken } = useAuthStore()

  // ---------------------------------------------------------------------
  // DEFAULT — "Lịch ca tuần này" (week schedule grid)
  // ---------------------------------------------------------------------
  const [weekSchedule, setWeekSchedule] = useState<WeekScheduleDay[] | null>(null)
  const [weekScheduleError, setWeekScheduleError] = useState<string | null>(null)

  const fetchWeekSchedule = useCallback(async () => {
    if (!accessToken) return
    setWeekScheduleError(null)
    try {
      const today = new Date()
      const start = startOfWeekMonday(today)
      const end = new Date(start)
      end.setDate(start.getDate() + 6)
      const startStr = toLocalISODate(start)
      const endStr = toLocalISODate(end)
      const todayStr = toLocalISODate(today)
      // scheduleApi.getMySchedule resolves directly to Schedule[] (apiRequest<T>
      // unwraps — same contract useAttendanceModernClock.ts already relies on
      // for the single-day today-shift lookup; CÙNG nguồn với màn Lịch per the
      // wireframe annotation, not the separate "ca sắp bắt đầu" source).
      const list = await scheduleApi.getMySchedule(accessToken, startStr, endStr)
      const scheduledDates = new Set((Array.isArray(list) ? list : []).map((s) => s.date))
      const days: WeekScheduleDay[] = []
      for (let i = 0; i < 7; i++) {
        const d = new Date(start)
        d.setDate(start.getDate() + i)
        const dateStr = toLocalISODate(d)
        days.push({ date: dateStr, hasShift: scheduledDates.has(dateStr), isToday: dateStr === todayStr })
      }
      setWeekSchedule(days)
    } catch {
      // "1 widget lỗi không sập cả trang" — same isolation principle as the
      // other independent fetches in useAttendanceModernClock.ts. Leave
      // weekSchedule null; the card renders nothing rather than fake data.
      setWeekScheduleError('Không tải được lịch ca tuần này.')
    }
  }, [accessToken])

  useEffect(() => {
    fetchWeekSchedule()
  }, [fetchWeekSchedule])

  // ---------------------------------------------------------------------
  // DEFAULT — "Việc khẩn cấp (N)" / "Việc phát sinh (N)"
  // ---------------------------------------------------------------------
  const [urgentTasks, setUrgentTasks] = useState<HomeTaskItem[]>([])
  const [adhocTasks, setAdhocTasks] = useState<HomeTaskItem[]>([])
  const [tasksError, setTasksError] = useState<string | null>(null)

  const fetchTasks = useCallback(async () => {
    setTasksError(null)
    try {
      // GET /api/v2/tasks/my — same endpoint + client-side URGENT/AD_HOC split
      // mobile uses (taskApi.ts getMyTasks + AttendanceScreen.tsx :686-714).
      // Sorted URGENT-first then by deadline asc server-side (tasks.py get_my_tasks).
      const res = await apiV2.get<TaskListApiResponse>('/tasks/my', { params: { limit: 20 } })
      const items = res.data?.items ?? []
      setUrgentTasks(
        items
          .filter((t) => t.task_type === 'URGENT')
          .map((t) => ({ id: t.id, title: t.title, deadline: t.deadline }))
      )
      setAdhocTasks(
        items
          .filter((t) => t.task_type === 'AD_HOC')
          .map((t) => ({ id: t.id, title: t.title, deadline: t.deadline }))
      )
    } catch {
      // Informational-only card (registry: "KHÔNG chặn chấm công, chỉ để biết
      // trước") — a fetch failure must not block clock-in; just show nothing.
      setTasksError('Không tải được danh sách công việc.')
      setUrgentTasks([])
      setAdhocTasks([])
    }
  }, [])

  useEffect(() => {
    fetchTasks()
  }, [fetchTasks])

  // ---------------------------------------------------------------------
  // STALE-OPEN — "còn ca hôm qua chưa chấm công ra"
  // ---------------------------------------------------------------------
  const [staleOpen, setStaleOpen] = useState<StaleOpenInfo | null>(null)
  const [staleOpenChecked, setStaleOpenChecked] = useState(false)

  /**
   * Derives "≥1 attendance record with clock_out IS NULL from BEFORE today"
   * from the real self-history endpoint instead of the (backend-unimplemented)
   * pre-checkin-view `is_stale` field — see file header for why. `end_date` is
   * set to YESTERDAY so today's own (possibly still-open) record is excluded
   * by the backend query itself (attendance.py get_attendance_history():
   * `Attendance.clock_in <= datetime.combine(end_date, datetime.max.time())`),
   * not by client-side date math. Records come back ordered by
   * `clock_in DESC` (backend `order_by(Attendance.clock_in.desc())`), so the
   * first `clock_out === null` hit is the most recent stale-open shift.
   */
  const checkStaleOpen = useCallback(async () => {
    try {
      const today = new Date()
      const yesterday = new Date(today)
      yesterday.setDate(today.getDate() - 1)
      const lookbackStart = new Date(today)
      lookbackStart.setDate(today.getDate() - 30) // bounded lookback — a genuinely stale shift is expected within days, not months
      const history = await managerProfileApi.getHistory(toLocalISODate(lookbackStart), toLocalISODate(yesterday))
      // #780: a pending_close row waits for the manager — not stale-open for the employee.
      const open = history.records.find((r) => r.clock_out === null && r.status !== 'pending_close')
      setStaleOpen(open ? { attendanceId: open.id, clockInAt: open.clock_in } : null)
    } catch {
      // Fetch failure — do not block the home screen behind an unresolvable
      // state; treat as "no known stale record" (same fail-open posture as
      // the other optional widgets on this page).
      setStaleOpen(null)
    } finally {
      setStaleOpenChecked(true)
    }
  }, [])

  useEffect(() => {
    checkStaleOpen()
  }, [checkStaleOpen])

  const staleOpenMinutes = useMemo(() => {
    if (!staleOpen) return null
    return Math.max(0, Math.round((Date.now() - new Date(staleOpen.clockInAt).getTime()) / 60000))
  }, [staleOpen])

  return {
    weekSchedule,
    weekScheduleError,
    urgentTasks,
    adhocTasks,
    tasksError,
    staleOpen,
    staleOpenChecked,
    staleOpenMinutes,
    recheckStaleOpen: checkStaleOpen,
  }
}
